import React, { useState, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Crown, TrendingUp, ShoppingBag, DollarSign, Download, ChevronDown, ChevronUp, X, AlertTriangle, Zap, Search, Star, Shield, Headphones, BarChart3, Megaphone, CreditCard, ArrowRight, XCircle, Wallet, Bell, Package, History, FileText, Info, ShieldCheck, Check } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { usePackages, BASE_PRICES, ReportPackage } from '../../contexts/PackagesContext';
interface SubscriptionTabProps {
  showToast: (msg: string) => void;
}
const PAYMENTS = [{
  id: 1,
  date: '15 Мар 2026',
  desc: 'Продление PRO',
  amount: '990 ₽',
  method: 'Visa ···4582',
  status: 'paid' as const
}, {
  id: 2,
  date: '15 Фев 2026',
  desc: 'Продление PRO',
  amount: '990 ₽',
  method: 'Visa ···4582',
  status: 'paid' as const
}, {
  id: 3,
  date: '15 Янв 2026',
  desc: 'Продление PRO',
  amount: '990 ₽',
  method: 'Visa ···4582',
  status: 'paid' as const
}, {
  id: 4,
  date: '15 Дек 2025',
  desc: 'Продление PRO',
  amount: '990 ₽',
  method: 'Visa ···4582',
  status: 'paid' as const
}, {
  id: 5,
  date: '15 Ноя 2025',
  desc: 'Переход на PRO',
  amount: '990 ₽',
  method: 'Visa ···4582',
  status: 'paid' as const
}];
const PLANS = [{
  id: 'free',
  name: 'Базовый',
  price: 0,
  priceLabel: '0 ₽',
  period: 'бесплатно',
  color: 'slate',
  features: {
    consultations: '5 / мес',
    commission: '25%',
    priority: 'Нет',
    limits: '3 услуги',
    advertising: 'Нет',
    analytics: 'Базовая',
    support: 'Email',
    packagesDiscount: 'Нет'
  },
  included: ['5 консультаций/мес', 'Комиссия 25%', 'Базовая аналитика', '3 услуги в каталоге'],
  missing: ['Приоритет в поиске', 'Продвижение', 'Расширенная аналитика']
}, {
  id: 'pro',
  name: 'PRO',
  price: 990,
  priceLabel: '990 ₽',
  period: '/мес',
  color: 'indigo',
  popular: true,
  features: {
    consultations: 'Безлимитно',
    commission: '15%',
    priority: 'Да',
    limits: '20 услуг',
    advertising: 'Базовое',
    analytics: 'Полная',
    support: 'Приоритетный',
    packagesDiscount: 'Скидка 10%'
  },
  included: ['Безлимитно консультаций', 'Комиссия 15%', 'Полная аналитика', '20 услуг', 'Приоритет в поиске', 'Базовое продвижение', 'Скидка 10% на пакеты отчетов'],
  missing: ['Топ в каталоге', 'Персональный менеджер']
}, {
  id: 'top',
  name: 'TOP',
  price: 2490,
  priceLabel: '2 490 ₽',
  period: '/мес',
  color: 'amber',
  features: {
    consultations: 'Безлимитно',
    commission: '10%',
    priority: 'Топ',
    limits: 'Безлимитно',
    advertising: 'Премиум',
    analytics: 'Полная + AI',
    support: 'Персональный менеджер',
    packagesDiscount: 'Скидка 20%'
  },
  included: ['Безлимитно всё', 'Комиссия 10%', 'AI-аналитика', 'Топ в каталоге', 'Премиум продвижение', 'Персональный менеджер', 'Скидка 20% на пакеты отчетов'],
  missing: []
}];
const FEATURE_ROWS = [{
  key: 'consultations',
  label: 'Консультации',
  icon: ShoppingBag
}, {
  key: 'commission',
  label: 'Комиссия',
  icon: DollarSign
}, {
  key: 'priority',
  label: 'Приоритет в поиске',
  icon: Search
}, {
  key: 'limits',
  label: 'Лимит услуг',
  icon: BarChart3
}, {
  key: 'advertising',
  label: 'Продвижение',
  icon: Megaphone
}, {
  key: 'analytics',
  label: 'Аналитика',
  icon: TrendingUp
}, {
  key: 'support',
  label: 'Поддержка',
  icon: Headphones
}, {
  key: 'packagesDiscount',
  label: 'Скидка на пакеты',
  icon: CreditCard
}];
export function SubscriptionTab({
  showToast
}: SubscriptionTabProps) {
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [expandedPayment, setExpandedPayment] = useState<number | null>(null);
  const [payFromBalance, setPayFromBalance] = useState(true);
  // Packages State
  const {
    packages,
    activePackages,
    totalRemaining,
    totalUsed,
    addPackage,
    getPackagePrice
  } = usePackages();
  const [historyModalPkg, setHistoryModalPkg] = useState<ReportPackage | null>(null);
  const [confirmPurchase, setConfirmPurchase] = useState<20 | 50 | 100 | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState<20 | 50 | 100 | null>(null);
  const [upgradePlan, setUpgradePlan] = useState<(typeof PLANS)[number] | null>(null);
  const [upgradePayMethod, setUpgradePayMethod] = useState<'balance' | 'card'>('balance');
  const [upgradeProcessing, setUpgradeProcessing] = useState(false);
  const currentPlanId = 'pro';
  const currentPlan = PLANS.find(p => p.id === currentPlanId)!;
  const accountBalance = 24500;
  const discount = currentPlanId === 'top' ? 20 : currentPlanId === 'pro' ? 10 : 0;
  const discountReason = `тарифу ${currentPlan.name}`;
  const togglePayment = (id: number) => {
    setExpandedPayment(prev => prev === id ? null : id);
  };
  const scrollToPackages = () => {
    const el = document.getElementById('buy-packages-section');
    el?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  const formatPrice = (price: number) => {
    return price.toLocaleString('ru-RU') + ' ₽';
  };
  const handleBuy = (type: 20 | 50 | 100) => {
    setConfirmPurchase(type);
    setPurchaseSuccess(null);
  };
  const handleConfirmPurchase = () => {
    if (!confirmPurchase) return;
    setIsProcessing(true);
    setTimeout(() => {
      addPackage(confirmPurchase, 'astrologer', discount, discountReason);
      setIsProcessing(false);
      setPurchaseSuccess(confirmPurchase);
      setConfirmPurchase(null);
      showToast(`Пакет на ${confirmPurchase} отчётов успешно приобретён!`);
    }, 1500);
  };
  return <div className="space-y-4 sm:space-y-6 animate-in fade-in duration-300" data-id="element-15714">
      {/* Header */}
      <div data-id="element-15715">
        <h2 className="text-lg sm:text-2xl font-bold text-slate-900" data-id="element-15716">
          Подписка и Пакеты
        </h2>
        <p className="text-slate-500 text-xs sm:text-sm mt-0.5" data-id="element-15717">
          Тариф, пакеты отчетов, платежи и выгода
        </p>
      </div>

      {/* Current Plan */}
      <Card padding="sm" className="border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-purple-50/30 sm:p-5" data-id="element-15718">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-3 sm:mb-5" data-id="element-15719">
          <div data-id="element-15720">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5" data-id="element-15721">
              <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" data-id="element-15722" />
              <h3 className="text-base sm:text-xl font-bold text-indigo-900" data-id="element-15723">
                Тариф {currentPlan.name}
              </h3>
              <Badge variant="pro" size="sm" data-id="element-15724">
                Активен
              </Badge>
            </div>
            <p className="text-xs sm:text-sm text-indigo-700" data-id="element-15725">
              Списание: 15 Апреля 2026 ·{' '}
              <span className="font-bold" data-id="element-15726">990 ₽/мес</span>
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto" data-id="element-15727">
            {cancelConfirm ? <div className="flex flex-col gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl w-full sm:w-auto" data-id="element-15728">
                <div className="flex items-center gap-2 text-sm text-rose-700 font-medium" data-id="element-15729">
                  <AlertTriangle className="w-4 h-4" data-id="element-15730" /> Вы потеряете:
                </div>
                <ul className="text-xs text-rose-600 space-y-0.5 ml-6" data-id="element-15731">
                  <li data-id="element-15732">• Приоритет в поиске</li>
                  <li data-id="element-15733">• Скидку {discount}% на пакеты</li>
                  <li data-id="element-15734">• Комиссия вырастет до 25%</li>
                </ul>
                <div className="flex gap-2 mt-1" data-id="element-15735">
                  <Button variant="outline" size="sm" className="border-rose-200 text-rose-600 hover:bg-rose-100" onClick={() => {
                setCancelConfirm(false);
                showToast('Подписка будет отменена в конце периода');
              }} data-id="element-15736">
                    Да, отменить
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCancelConfirm(false)} data-id="element-15737">
                    Оставить
                  </Button>
                </div>
              </div> : <>
                <Button variant="outline" size="sm" className="text-slate-500 flex-1 sm:flex-none" onClick={() => setCancelConfirm(true)} data-id="element-15738">
                  Отменить
                </Button>
                <Button variant="primary" size="sm" className="flex-1 sm:flex-none" icon={<Zap className="w-4 h-4" data-id="element-15740" />} onClick={() => {
              const el = document.getElementById('plans-section');
              el?.scrollIntoView({
                behavior: 'smooth'
              });
            }} data-id="element-15739">
                  Улучшить тариф
                </Button>
              </>}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-3 sm:mb-5" data-id="element-15741">
          <div className="bg-white/70 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-indigo-100" data-id="element-15742">
            <div className="text-[8px] sm:text-[10px] text-indigo-500 font-medium uppercase tracking-wider mb-0.5" data-id="element-15743">
              Заказы/мес
            </div>
            <div className="text-sm sm:text-lg font-bold text-indigo-900 flex items-center gap-0.5" data-id="element-15744">
              +12{' '}
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" data-id="element-15745" />
            </div>
          </div>
          <div className="bg-white/70 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-indigo-100" data-id="element-15746">
            <div className="text-[8px] sm:text-[10px] text-indigo-500 font-medium uppercase tracking-wider mb-0.5" data-id="element-15747">
              Доход/мес
            </div>
            <div className="text-sm sm:text-lg font-bold text-indigo-900" data-id="element-15748">
              68 400 ₽
            </div>
          </div>
          <div className="bg-white/70 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-indigo-100" data-id="element-15749">
            <div className="text-[8px] sm:text-[10px] text-indigo-500 font-medium uppercase tracking-wider mb-0.5" data-id="element-15750">
              Комиссия
            </div>
            <div className="text-sm sm:text-lg font-bold text-indigo-900" data-id="element-15751">
              15%
            </div>
          </div>
          <div className="bg-white/70 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-indigo-100" data-id="element-15752">
            <div className="text-[8px] sm:text-[10px] text-indigo-500 font-medium uppercase tracking-wider mb-0.5" data-id="element-15753">
              Приоритет
            </div>
            <div className="text-sm sm:text-lg font-bold text-emerald-600 flex items-center gap-0.5" data-id="element-15754">
              <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-15755" /> Да
            </div>
          </div>
        </div>

        {/* What's included */}
        <div className="pt-3 sm:pt-4 border-t border-indigo-100" data-id="element-15756">
          <div className="text-[10px] sm:text-xs font-bold text-indigo-700 uppercase tracking-wider mb-1.5 sm:mb-2" data-id="element-15757">
            Что входит в {currentPlan.name}
          </div>
          <div className="grid grid-cols-2 gap-1" data-id="element-15758">
            {currentPlan.included.map(f => {
            const isPackageDiscount = f.includes('Скидка') && f.includes('пакеты');
            return <div key={f} className={`flex items-center gap-1 text-xs sm:text-sm ${isPackageDiscount ? 'text-indigo-900 font-bold cursor-pointer hover:text-indigo-600 transition-colors' : 'text-indigo-800'}`} onClick={isPackageDiscount ? scrollToPackages : undefined} data-id="element-15759">
                  <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-500 shrink-0" data-id="element-15760" />{' '}
                  {f}
                  {isPackageDiscount && <ArrowRight className="w-3 h-3 ml-0.5" data-id="element-15761" />}
                </div>;
          })}
          </div>
        </div>
      </Card>

      {/* Payment Method & Notifications */}
      <Card padding="sm" className="sm:p-5" data-id="element-15762">
        <h3 className="font-bold text-slate-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base" data-id="element-15763">
          <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" data-id="element-15764" /> Способ
          оплаты подписки
        </h3>
        <div className="flex gap-2 sm:gap-3 mb-3 sm:mb-4" data-id="element-15765">
          <button onClick={() => {
          setPayFromBalance(true);
          showToast('Подписка будет списываться с баланса');
        }} className={`flex-1 p-3 rounded-xl border text-left transition-colors ${payFromBalance ? 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-15766">
            <div className="flex items-center gap-2 mb-1" data-id="element-15767">
              <Wallet className="w-4 h-4 text-indigo-600" data-id="element-15768" />
              <span className="font-bold text-sm text-slate-900" data-id="element-15769">
                С баланса
              </span>
              {payFromBalance && <Badge variant="success" size="sm" data-id="element-15770">
                  Активно
                </Badge>}
            </div>
            <div className="text-xs text-slate-500" data-id="element-15771">
              Баланс:{' '}
              <span className="font-bold text-slate-700" data-id="element-15772">
                {new Intl.NumberFormat('ru-RU').format(accountBalance)} ₽
              </span>
            </div>
          </button>
          <button onClick={() => {
          setPayFromBalance(false);
          showToast('Подписка будет списываться с карты');
        }} className={`flex-1 p-3 rounded-xl border text-left transition-colors ${!payFromBalance ? 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-15773">
            <div className="flex items-center gap-2 mb-1" data-id="element-15774">
              <CreditCard className="w-4 h-4 text-slate-600" data-id="element-15775" />
              <span className="font-bold text-sm text-slate-900" data-id="element-15776">С карты</span>
              {!payFromBalance && <Badge variant="success" size="sm" data-id="element-15777">
                  Активно
                </Badge>}
            </div>
            <div className="text-xs text-slate-500" data-id="element-15778">Visa •••• 4582</div>
          </button>
        </div>
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg" data-id="element-15779">
          <Bell className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" data-id="element-15780" />
          <div className="text-xs text-amber-700" data-id="element-15781">
            <span className="font-medium" data-id="element-15782">Уведомление:</span> за 3 дня до
            списания вы получите напоминание. Следующее списание{' '}
            <span className="font-bold" data-id="element-15783">990 ₽</span> — 15 Апреля 2026{' '}
            {payFromBalance ? 'с баланса' : 'с карты'}.
          </div>
        </div>
      </Card>

      {/* Why Upgrade */}
      <Card padding="sm" className="border-amber-200 bg-amber-50/30 sm:p-5" data-id="element-15784">
        <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3" data-id="element-15785">
          <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" data-id="element-15786" />
          <h3 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-15787">
            Почему стоит улучшить тариф
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3" data-id="element-15788">
          {[{
          stat: '+40%',
          label: 'заказов',
          desc: 'На TOP на 40% больше заказов'
        }, {
          stat: 'Топ-3',
          label: 'в каталоге',
          desc: 'Профиль первым в поиске'
        }, {
          stat: '-10%',
          label: 'комиссия',
          desc: 'Экономия на комиссии'
        }].map(item => <div key={item.label} className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 border border-amber-100" data-id="element-15789">
              <div className="text-lg sm:text-2xl font-bold text-amber-700 mb-0.5" data-id="element-15790">
                {item.stat}
              </div>
              <div className="text-xs sm:text-sm font-medium text-slate-900 mb-0.5" data-id="element-15791">
                {item.label}
              </div>
              <div className="text-[10px] sm:text-xs text-slate-500 hidden sm:block" data-id="element-15792">
                {item.desc}
              </div>
            </div>)}
        </div>
        <Button variant="primary" size="sm" className="mt-3 sm:mt-4 bg-amber-600 hover:bg-amber-700" icon={<ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-15794" />} onClick={() => {
        const el = document.getElementById('plans-section');
        el?.scrollIntoView({
          behavior: 'smooth'
        });
      }} data-id="element-15793">
          Перейти на TOP
        </Button>
      </Card>

      {/* Plan Comparison */}
      <div id="plans-section" data-id="element-15795">
        <h3 className="font-bold text-slate-900 text-sm sm:text-lg mb-3 sm:mb-4" data-id="element-15796">
          Выбор тарифа
        </h3>

        {/* Mobile: Cards */}
        <div className="sm:hidden space-y-2" data-id="element-15797">
          {PLANS.map(plan => {
          const isCurrent = plan.id === 'pro';
          return <Card key={plan.id} padding="sm" className={`${isCurrent ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-slate-200'} ${plan.id === 'top' ? 'border-amber-400' : ''}`} data-id="element-15798">
                <div className="flex items-center justify-between mb-1.5" data-id="element-15799">
                  <div className="flex items-center gap-1.5" data-id="element-15800">
                    <span className="font-bold text-base text-slate-900" data-id="element-15801">
                      {plan.name}
                    </span>
                    {isCurrent && <Badge variant="pro" size="sm" data-id="element-15802">
                        Текущий
                      </Badge>}
                    {plan.id === 'top' && !isCurrent && <Badge variant="warning" size="sm" data-id="element-15803">
                        Популярный
                      </Badge>}
                  </div>
                </div>
                <div className="mb-2" data-id="element-15804">
                  <span className="text-xl font-bold text-slate-900" data-id="element-15805">
                    {plan.priceLabel}
                  </span>
                  <span className="text-xs text-slate-500" data-id="element-15806">{plan.period}</span>
                </div>
                <ul className="space-y-1 mb-3" data-id="element-15807">
                  {plan.included.map(f => <li key={f} className="text-xs text-slate-700 flex items-center gap-1" data-id="element-15808">
                      <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-15809" />{' '}
                      {f}
                    </li>)}
                  {plan.missing.map(f => <li key={f} className="text-xs text-slate-400 flex items-center gap-1" data-id="element-15810">
                      <XCircle className="w-3 h-3 text-slate-300 shrink-0" data-id="element-15811" />{' '}
                      {f}
                    </li>)}
                </ul>
                {isCurrent ? <div className="text-center text-xs text-indigo-600 font-medium py-1.5" data-id="element-15812">
                    Ваш текущий тариф
                  </div> : <Button variant={plan.id === 'top' ? 'primary' : 'outline'} fullWidth size="sm" onClick={() => setUpgradePlan(plan)} data-id="element-15813">
                    {plan.price > 990 ? 'Улучшить' : plan.price === 0 ? 'Перейти' : 'Выбрать'}
                  </Button>}
              </Card>;
        })}
        </div>

        {/* Desktop: Comparison Table */}
        <Card padding="none" className="hidden sm:block overflow-hidden" data-id="element-15814">
          <table className="w-full text-sm" data-id="element-15815">
            <thead data-id="element-15816">
              <tr className="border-b border-slate-100" data-id="element-15817">
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 w-1/4" data-id="element-15818">
                  Возможность
                </th>
                {PLANS.map(plan => <th key={plan.id} className={`px-4 py-3 text-center ${plan.id === 'pro' ? 'bg-indigo-50/50' : plan.id === 'top' ? 'bg-amber-50/50' : ''}`} data-id="element-15819">
                    <div className="font-bold text-slate-900" data-id="element-15820">{plan.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5" data-id="element-15821">
                      {plan.priceLabel}
                      {plan.period}
                    </div>
                    {plan.id === 'pro' && <Badge variant="pro" size="sm" className="mt-1" data-id="element-15822">
                        Текущий
                      </Badge>}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-15823">
              {FEATURE_ROWS.map(row => <tr key={row.key} className="hover:bg-slate-50/50" data-id="element-15824">
                  <td className="px-4 py-2.5 text-slate-700 flex items-center gap-2" data-id="element-15825">
                    <row.icon className="w-4 h-4 text-slate-400" data-id="element-15826" /> {row.label}
                  </td>
                  {PLANS.map(plan => <td key={plan.id} className={`px-4 py-2.5 text-center font-medium ${plan.id === 'pro' ? 'bg-indigo-50/30 text-indigo-700' : plan.id === 'top' ? 'bg-amber-50/30 text-amber-700' : 'text-slate-600'}`} data-id="element-15827">
                      {(plan.features as Record<string, string>)[row.key]}
                    </td>)}
                </tr>)}
            </tbody>
            <tfoot data-id="element-15828">
              <tr className="border-t border-slate-200" data-id="element-15829">
                <td className="px-4 py-3" data-id="element-15830"></td>
                {PLANS.map(plan => <td key={plan.id} className={`px-4 py-3 text-center ${plan.id === 'pro' ? 'bg-indigo-50/30' : plan.id === 'top' ? 'bg-amber-50/30' : ''}`} data-id="element-15831">
                    {plan.id === 'pro' ? <span className="text-sm text-indigo-600 font-medium" data-id="element-15832">
                        Текущий тариф
                      </span> : <Button variant={plan.id === 'top' ? 'primary' : 'outline'} size="sm" fullWidth onClick={() => setUpgradePlan(plan)} data-id="element-15833">
                        {plan.price > 990 ? 'Улучшить' : 'Выбрать'}
                      </Button>}
                  </td>)}
              </tr>
            </tfoot>
          </table>
        </Card>
      </div>

      {/* Payment History */}
      <div data-id="element-15834">
        <h3 className="font-bold text-slate-900 text-lg mb-4" data-id="element-15835">
          История платежей
        </h3>
        <Card padding="none" className="overflow-hidden" data-id="element-15836">
          {/* Desktop */}
          <div className="hidden sm:block" data-id="element-15837">
            <table className="w-full text-sm text-left" data-id="element-15838">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-100" data-id="element-15839">
                <tr data-id="element-15840">
                  <th className="px-4 py-2.5 font-medium text-xs" data-id="element-15841">Дата</th>
                  <th className="px-4 py-2.5 font-medium text-xs" data-id="element-15842">Описание</th>
                  <th className="px-4 py-2.5 font-medium text-xs" data-id="element-15843">Способ</th>
                  <th className="px-4 py-2.5 font-medium text-xs" data-id="element-15844">Сумма</th>
                  <th className="px-4 py-2.5 font-medium text-xs" data-id="element-15845">Статус</th>
                  <th className="px-4 py-2.5 font-medium text-xs" data-id="element-15846"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-15847">
                {PAYMENTS.map(p => <Fragment key={p.id} data-id="element-15848">
                    <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => togglePayment(p.id)} data-id="element-15849">
                      <td className="px-4 py-2.5 text-slate-600 text-xs" data-id="element-15850">
                        {p.date}
                      </td>
                      <td className="px-4 py-2.5 font-medium text-slate-900 text-xs" data-id="element-15851">
                        {p.desc}
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 text-xs" data-id="element-15852">
                        {p.method}
                      </td>
                      <td className="px-4 py-2.5 text-slate-900 text-xs font-medium" data-id="element-15853">
                        {p.amount}
                      </td>
                      <td className="px-4 py-2.5" data-id="element-15854">
                        <Badge variant="success" size="sm" data-id="element-15855">
                          Оплачено
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5" data-id="element-15856">
                        {expandedPayment === p.id ? <ChevronUp className="w-4 h-4 text-slate-400" data-id="element-15857" /> : <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-15858" />}
                      </td>
                    </tr>
                    {expandedPayment === p.id && <tr data-id="element-15859">
                        <td colSpan={6} className="px-4 py-3 bg-slate-50" data-id="element-15860">
                          <div className="flex items-center justify-between" data-id="element-15861">
                            <div className="text-xs text-slate-500 space-y-1" data-id="element-15862">
                              <div data-id="element-15863">
                                Чек №{1000 + p.id} · {p.date}
                              </div>
                              <div data-id="element-15864">Способ оплаты: {p.method}</div>
                              <div data-id="element-15865">Тариф: PRO · Период: 1 месяц</div>
                            </div>
                            <Button variant="outline" size="sm" icon={<Download className="w-3.5 h-3.5" data-id="element-15867" />} onClick={e => {
                        e.stopPropagation();
                        showToast('Чек скачан ✓');
                      }} data-id="element-15866">
                              Скачать чек
                            </Button>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>)}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="sm:hidden divide-y divide-slate-100" data-id="element-15868">
            {PAYMENTS.map(p => <div key={p.id} data-id="element-15869">
                <div className="p-3 flex justify-between items-center cursor-pointer" onClick={() => togglePayment(p.id)} data-id="element-15870">
                  <div data-id="element-15871">
                    <div className="text-sm font-medium text-slate-900" data-id="element-15872">
                      {p.desc}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-15873">
                      {p.date} · {p.method}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-15874">
                    <div className="text-right" data-id="element-15875">
                      <div className="text-sm font-bold text-slate-900" data-id="element-15876">
                        {p.amount}
                      </div>
                      <Badge variant="success" size="sm" data-id="element-15877">
                        Оплачено
                      </Badge>
                    </div>
                    {expandedPayment === p.id ? <ChevronUp className="w-4 h-4 text-slate-400" data-id="element-15878" /> : <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-15879" />}
                  </div>
                </div>
                {expandedPayment === p.id && <div className="px-3 pb-3" data-id="element-15880">
                    <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-id="element-15881">
                      <div className="text-xs text-slate-500" data-id="element-15882">
                        Чек №{1000 + p.id} · Тариф PRO · 1 месяц
                      </div>
                      <Button variant="outline" size="sm" fullWidth icon={<Download className="w-3.5 h-3.5" data-id="element-15884" />} onClick={() => showToast('Чек скачан ✓')} data-id="element-15883">
                        Скачать чек
                      </Button>
                    </div>
                  </div>}
              </div>)}
          </div>
        </Card>
      </div>

      {/* Packages Summary */}
      <Card padding="sm" className="border-slate-200 sm:p-5" data-id="element-15885">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6" data-id="element-15886">
          <div className="flex items-center gap-2 sm:gap-3" data-id="element-15887">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600" data-id="element-15888">
              <Package className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-15889" />
            </div>
            <div data-id="element-15890">
              <h3 className="text-sm sm:text-lg font-bold text-slate-900" data-id="element-15891">
                Сводка по пакетам
              </h3>
              <p className="text-xs text-slate-500" data-id="element-15892">Управление отчетами</p>
            </div>
          </div>
          {discount > 0 && <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2 sm:p-3 text-right" data-id="element-15893">
              <div className="text-xs sm:text-sm font-bold text-emerald-700" data-id="element-15894">
                Скидка на пакеты: {discount}%
              </div>
              <div className="text-[10px] sm:text-xs text-emerald-600" data-id="element-15895">
                По тарифу {currentPlan.name}
              </div>
            </div>}
        </div>

        {activePackages.length > 0 ? <div className="space-y-4" data-id="element-15896">
            {activePackages.map(pkg => {
          const progress = pkg.usedReports / pkg.totalReports * 100;
          return <div key={pkg.id} className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-15897">
                  <div className="flex justify-between items-center mb-3" data-id="element-15898">
                    <div className="font-bold text-slate-900 text-lg" data-id="element-15899">
                      {pkg.title}
                    </div>
                    <Badge variant="success" data-id="element-15900">Активен</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-15901">
                    <div data-id="element-15902">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-15903">
                        Всего отчетов
                      </div>
                      <div className="font-bold text-slate-900" data-id="element-15904">
                        {pkg.totalReports}
                      </div>
                    </div>
                    <div data-id="element-15905">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-15906">
                        Использовано
                      </div>
                      <div className="font-bold text-slate-900" data-id="element-15907">
                        {pkg.usedReports}
                      </div>
                    </div>
                    <div data-id="element-15908">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-15909">
                        Осталось
                      </div>
                      <div className="font-bold text-indigo-600 text-lg" data-id="element-15910">
                        {pkg.remainingReports}
                      </div>
                    </div>
                  </div>
                  <div data-id="element-15911">
                    <div className="flex justify-between text-xs font-medium mb-1.5" data-id="element-15912">
                      <span className="text-slate-500" data-id="element-15913">
                        Прогресс использования
                      </span>
                      <span className="text-slate-900" data-id="element-15914">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden" data-id="element-15915">
                      <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{
                  width: `${progress}%`
                }} data-id="element-15916" />
                    </div>
                  </div>
                </div>;
        })}
          </div> : <div className="py-8 bg-slate-50 rounded-xl border border-slate-100 border-dashed" data-id="element-15917">
            <div className="text-center mb-6" data-id="element-15918">
              <div className="text-4xl mb-3" data-id="element-15919">📦</div>
              <h4 className="text-slate-900 font-bold text-lg mb-2" data-id="element-15920">
                У вас пока нет пакетов отчётов
              </h4>
              <p className="text-sm text-slate-500 max-w-md mx-auto" data-id="element-15921">
                Пакет — это набор предоплаченных отчётов. Вместо оплаты каждого
                отчёта отдельно, купите пакет и экономьте до 20%.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 px-6 mb-6" data-id="element-15922">
              <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-15923">
                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold" data-id="element-15924">
                  1
                </div>
                Купите пакет
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 hidden sm:block" data-id="element-15925" />
              <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-15926">
                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold" data-id="element-15927">
                  2
                </div>
                Создавайте отчёты
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 hidden sm:block" data-id="element-15928" />
              <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-15929">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-xs font-bold" data-id="element-15930">
                  ✓
                </div>
                Экономьте
              </div>
            </div>
            {discount > 0 && <div className="text-center mb-4" data-id="element-15931">
                <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200" data-id="element-15932">
                  Ваша скидка {discount}% по тарифу {currentPlan.name} уже
                  активна
                </span>
              </div>}
            <div className="text-center" data-id="element-15933">
              <Button variant="primary" onClick={scrollToPackages} data-id="element-15934">
                Выбрать пакет
              </Button>
            </div>
          </div>}
      </Card>

      {/* Active Packages List */}
      {activePackages.length > 0 && <div data-id="element-15935">
          <h3 className="font-bold text-slate-900 text-lg mb-4" data-id="element-15936">
            Активные пакеты
          </h3>
          <div className="space-y-4" data-id="element-15937">
            {activePackages.map(pkg => {
          const progress = pkg.usedReports / pkg.totalReports * 100;
          const basePrice = BASE_PRICES[pkg.type];
          return <Card key={pkg.id} className="border-slate-200 overflow-hidden" data-id="element-15938">
                  <div className="p-5 flex flex-col md:flex-row justify-between gap-6" data-id="element-15939">
                    <div className="flex-1" data-id="element-15940">
                      <div className="flex items-center gap-3 mb-3" data-id="element-15941">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0" data-id="element-15942">
                          <Package className="w-5 h-5" data-id="element-15943" />
                        </div>
                        <div data-id="element-15944">
                          <h4 className="text-lg font-bold text-slate-900" data-id="element-15945">
                            {pkg.title}
                          </h4>
                          <div className="text-xs text-slate-500" data-id="element-15946">
                            Куплен: {formatDate(pkg.purchaseDate)}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-15947">
                        <div data-id="element-15948">
                          <div className="text-xs text-slate-500 mb-1" data-id="element-15949">
                            Обычная цена
                          </div>
                          <div className="text-sm text-slate-400 line-through" data-id="element-15950">
                            {formatPrice(basePrice)}
                          </div>
                        </div>
                        <div data-id="element-15951">
                          <div className="text-xs text-slate-500 mb-1" data-id="element-15952">
                            Ваша цена
                          </div>
                          <div className="text-sm font-bold text-slate-900" data-id="element-15953">
                            {formatPrice(pkg.purchasePrice)}
                          </div>
                        </div>
                        <div data-id="element-15954">
                          <div className="text-xs text-slate-500 mb-1" data-id="element-15955">
                            Использовано
                          </div>
                          <div className="text-sm font-medium text-slate-900" data-id="element-15956">
                            {pkg.usedReports}
                          </div>
                        </div>
                        <div data-id="element-15957">
                          <div className="text-xs text-slate-500 mb-1" data-id="element-15958">
                            Осталось
                          </div>
                          <div className="text-sm font-bold text-indigo-600" data-id="element-15959">
                            {pkg.remainingReports}
                          </div>
                        </div>
                      </div>

                      <div className="text-xs text-slate-500 mb-4" data-id="element-15960">
                        Цена за 1 отчет:{' '}
                        <span className="font-bold text-slate-900" data-id="element-15961">
                          {formatPrice(pkg.pricePerReport)}
                        </span>
                      </div>

                      <div className="mb-4" data-id="element-15962">
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-15963">
                          <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{
                      width: `${progress}%`
                    }} data-id="element-15964" />
                        </div>
                      </div>

                      <div className="flex gap-3" data-id="element-15965">
                        <Button variant="outline" size="sm" icon={<History className="w-4 h-4" data-id="element-15967" />} onClick={() => setHistoryModalPkg(pkg)} data-id="element-15966">
                          История использования
                        </Button>
                        <Button variant="primary" size="sm" onClick={scrollToPackages} data-id="element-15968">
                          Купить еще
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>;
        })}
          </div>
        </div>}

      {/* Buy Packages */}
      <div id="buy-packages-section" className="pt-2 sm:pt-4" data-id="element-15969">
        {/* Section Header with Explanation */}
        <div className="mb-4 sm:mb-6" data-id="element-15970">
          <h3 className="font-bold text-slate-900 text-sm sm:text-lg mb-1 sm:mb-2" data-id="element-15971">
            Пакеты отчётов
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 max-w-2xl" data-id="element-15972">
            Предоплаченный набор отчётов. Чем больше пакет — тем дешевле каждый
            отчёт.
          </p>
        </div>

        {/* What's in a package - visual explainer */}
        <Card padding="sm" className="mb-4 sm:mb-6 bg-gradient-to-r from-indigo-50/50 to-purple-50/30 border-indigo-100 sm:p-5" data-id="element-15973">
          <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-4" data-id="element-15974">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" data-id="element-15975" />
            <h4 className="font-bold text-slate-900 text-xs sm:text-base" data-id="element-15976">
              Какие отчёты входят в пакет?
            </h4>
          </div>
          <p className="text-xs text-slate-600 mb-2 sm:mb-4" data-id="element-15977">
            1 отчёт = 1 списание. Вы выбираете тип:
          </p>
          <div className="grid grid-cols-5 gap-1.5 sm:gap-3" data-id="element-15978">
            {[{
            icon: '🌟',
            name: 'Натальная',
            desc: 'Разбор личности'
          }, {
            icon: '☀️',
            name: 'Соляр',
            desc: 'Прогноз на год'
          }, {
            icon: '💕',
            name: 'Совмест.',
            desc: 'Синастрия'
          }, {
            icon: '🔮',
            name: 'Транзиты',
            desc: 'Влияния'
          }, {
            icon: '📊',
            name: 'Комбо',
            desc: 'Расширенный'
          }].map(report => <div key={report.name} className="bg-white rounded-lg sm:rounded-xl p-1.5 sm:p-3 border border-indigo-100/50 text-center" data-id="element-15979">
                <div className="text-base sm:text-2xl mb-0.5 sm:mb-1.5" data-id="element-15980">
                  {report.icon}
                </div>
                <div className="text-[9px] sm:text-xs font-bold text-slate-900 leading-tight" data-id="element-15981">
                  {report.name}
                </div>
                <div className="text-[8px] sm:text-[10px] text-slate-500 hidden sm:block" data-id="element-15982">
                  {report.desc}
                </div>
              </div>)}
          </div>
        </Card>

        {/* How it works - 3 steps */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-8" data-id="element-15983">
          <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-4 bg-white rounded-lg sm:rounded-xl border border-slate-100" data-id="element-15984">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-[10px] sm:text-sm flex-shrink-0" data-id="element-15985">
              1
            </div>
            <div data-id="element-15986">
              <div className="text-xs sm:text-sm font-bold text-slate-900" data-id="element-15987">
                Купите
              </div>
              <div className="text-[10px] sm:text-xs text-slate-500 hidden sm:block" data-id="element-15988">
                20, 50 или 100 отчётов
              </div>
            </div>
          </div>
          <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-4 bg-white rounded-lg sm:rounded-xl border border-slate-100" data-id="element-15989">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-[10px] sm:text-sm flex-shrink-0" data-id="element-15990">
              2
            </div>
            <div data-id="element-15991">
              <div className="text-xs sm:text-sm font-bold text-slate-900" data-id="element-15992">
                Создавайте
              </div>
              <div className="text-[10px] sm:text-xs text-slate-500 hidden sm:block" data-id="element-15993">
                Автосписание из пакета
              </div>
            </div>
          </div>
          <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-4 bg-white rounded-lg sm:rounded-xl border border-slate-100" data-id="element-15994">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-[10px] sm:text-sm flex-shrink-0" data-id="element-15995">
              ✓
            </div>
            <div data-id="element-15996">
              <div className="text-xs sm:text-sm font-bold text-slate-900" data-id="element-15997">
                Бессрочно
              </div>
              <div className="text-[10px] sm:text-xs text-slate-500 hidden sm:block" data-id="element-15998">
                Не сгорают со временем
              </div>
            </div>
          </div>
        </div>

        {/* Price comparison hint */}
        {discount > 0 && <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4 p-2 sm:p-3 bg-emerald-50 border border-emerald-100 rounded-lg" data-id="element-15999">
            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600 flex-shrink-0" data-id="element-16000" />
            <span className="text-xs sm:text-sm text-emerald-800" data-id="element-16001">
              Тариф <span className="font-bold" data-id="element-16002">{currentPlan.name}</span> —
              скидка <span className="font-bold" data-id="element-16003">{discount}%</span> на все
              пакеты.
            </span>
          </div>}

        {/* Package Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6" data-id="element-16004">
          {[20, 50, 100].map(type => {
          const basePrice = BASE_PRICES[type as 20 | 50 | 100];
          const discountedPrice = getPackagePrice(type as 20 | 50 | 100, discount);
          const savings = basePrice - discountedPrice;
          const perReport = Math.round(discountedPrice / type);
          const basePerReport = Math.round(basePrice / type);
          const savingsPercent = Math.round(savings / basePrice * 100);
          const isPopular = type === 50;
          return <Card key={type} className={`flex flex-col overflow-hidden ${isPopular ? 'border-indigo-400 shadow-lg relative transform md:-translate-y-2' : 'border-slate-200'}`} data-id="element-16005">
                {/* Card Header */}
                <div className={`p-3 sm:p-5 pb-2 sm:pb-4 ${isPopular ? 'bg-gradient-to-br from-indigo-50 to-purple-50' : 'bg-slate-50/50'}`} data-id="element-16006">
                  {isPopular && <div className="absolute right-3 sm:right-4 top-0" data-id="element-16007">
                      <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-[9px] sm:text-[10px] font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-b-lg shadow-sm" data-id="element-16008">
                        Популярно
                      </span>
                    </div>}
                  <div className="text-xl sm:text-3xl mb-1 sm:mb-2" data-id="element-16009">
                    {type === 20 ? '📦' : type === 50 ? '🚀' : '💎'}
                  </div>
                  <h4 className="text-base sm:text-xl font-bold text-slate-900 mb-0.5" data-id="element-16010">
                    {type === 20 ? 'Start Pack' : type === 50 ? 'Pro Pack' : 'Max Pack'}
                  </h4>
                  <div className="text-xs sm:text-sm text-slate-600 font-medium" data-id="element-16011">
                    {type} отчётов любого типа
                  </div>
                </div>

                {/* Pricing */}
                <div className="p-3 sm:p-5 flex-grow flex flex-col" data-id="element-16012">
                  <div className="mb-3 sm:mb-5" data-id="element-16013">
                    {discount > 0 ? <>
                        <div className="flex items-baseline gap-1.5 mb-0.5" data-id="element-16014">
                          <span className="text-xs text-slate-400 line-through" data-id="element-16015">
                            {formatPrice(basePrice)}
                          </span>
                          <span className="text-[10px] sm:text-xs font-bold text-emerald-600 bg-emerald-50 px-1 sm:px-1.5 py-0.5 rounded" data-id="element-16016">
                            -{savingsPercent}%
                          </span>
                        </div>
                        <div className="text-2xl sm:text-3xl font-bold text-indigo-600 mb-0.5" data-id="element-16017">
                          {formatPrice(discountedPrice)}
                        </div>
                        <div className="text-[10px] sm:text-xs text-emerald-600 font-medium" data-id="element-16018">
                          Экономия {formatPrice(savings)}
                        </div>
                      </> : <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-0.5" data-id="element-16019">
                        {formatPrice(basePrice)}
                      </div>}
                  </div>

                  {/* Key details */}
                  <div className="space-y-1.5 sm:space-y-3 mb-3 sm:mb-5 flex-grow" data-id="element-16020">
                    <div className="flex items-center justify-between py-1 sm:py-2 border-b border-slate-100" data-id="element-16021">
                      <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-16022">
                        <FileText className="w-3 h-3 text-slate-400" data-id="element-16023" /> За 1
                        отчёт
                      </span>
                      <div className="text-right" data-id="element-16024">
                        {discount > 0 && <span className="text-[10px] text-slate-400 line-through mr-1" data-id="element-16025">
                            {formatPrice(basePerReport)}
                          </span>}
                        <span className="font-bold text-xs sm:text-sm text-slate-900" data-id="element-16026">
                          {formatPrice(perReport)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between py-1 sm:py-2 border-b border-slate-100" data-id="element-16027">
                      <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-16028">
                        <Shield className="w-3 h-3 text-slate-400" data-id="element-16029" /> Срок
                      </span>
                      <span className="font-medium text-slate-900 text-xs" data-id="element-16030">
                        Бессрочно ∞
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1 sm:py-2 border-b border-slate-100" data-id="element-16031">
                      <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-16032">
                        <Star className="w-3 h-3 text-slate-400" data-id="element-16033" /> Отчёты
                      </span>
                      <span className="font-medium text-slate-900 text-xs" data-id="element-16034">
                        Все виды
                      </span>
                    </div>
                  </div>

                  {/* What's included mini-list - hidden on mobile for compactness */}
                  <div className="mb-3 sm:mb-5 p-2 sm:p-3 bg-slate-50 rounded-lg hidden sm:block" data-id="element-16035">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-16036">
                      Что входит
                    </div>
                    <div className="space-y-1" data-id="element-16037">
                      {['Натальные карты', 'Соляры', 'Совместимость', 'Транзиты', 'Комбо-анализ'].map(item => <div key={item} className="flex items-center gap-1 text-xs text-slate-600" data-id="element-16038">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" data-id="element-16039" />{' '}
                          {item}
                        </div>)}
                    </div>
                  </div>

                  <Button variant={isPopular ? 'primary' : 'outline'} fullWidth size="sm" onClick={() => handleBuy(type as 20 | 50 | 100)} data-id="element-16040">
                    Купить за{' '}
                    {formatPrice(discount > 0 ? discountedPrice : basePrice)}
                  </Button>
                </div>
              </Card>;
        })}
        </div>
      </div>

      {/* Modals */}
      {/* History Modal */}
      {historyModalPkg && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md overflow-y-auto" data-id="element-16041">
            <div className="bg-white rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.3)] max-w-2xl w-full max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200 ring-1 ring-black/5" data-id="element-16042">
              <div className="flex items-center justify-between p-5 border-b border-slate-100" data-id="element-16043">
                <div data-id="element-16044">
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-16045">
                    История использования
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-16046">
                    {historyModalPkg.title}
                  </p>
                </div>
                <button onClick={() => setHistoryModalPkg(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-16047">
                  <X className="w-5 h-5" data-id="element-16048" />
                </button>
              </div>

              <div className="p-5 overflow-y-auto" data-id="element-16049">
                {historyModalPkg.deductions.length === 0 ? <div className="text-center py-8" data-id="element-16050">
                    <p className="text-slate-500" data-id="element-16051">
                      Вы еще не использовали отчеты из этого пакета
                    </p>
                  </div> : <table className="w-full text-sm text-left" data-id="element-16052">
                    <thead className="bg-slate-50 text-slate-500 border-b border-slate-100" data-id="element-16053">
                      <tr data-id="element-16054">
                        <th className="px-4 py-2.5 font-medium text-xs" data-id="element-16055">
                          Дата
                        </th>
                        <th className="px-4 py-2.5 font-medium text-xs" data-id="element-16056">
                          Заказ
                        </th>
                        <th className="px-4 py-2.5 font-medium text-xs" data-id="element-16057">
                          Отчет
                        </th>
                        <th className="px-4 py-2.5 font-medium text-xs text-right" data-id="element-16058">
                          Списание
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-16059">
                      {historyModalPkg.deductions.map((deduction, idx) => {
                // Calculate remaining at that point in time (simplified for mock)
                const remainingAtTime = historyModalPkg.totalReports - (historyModalPkg.deductions.length - idx);
                return <tr key={deduction.id} className="hover:bg-slate-50" data-id="element-16060">
                            <td className="px-4 py-3 text-slate-600 text-xs" data-id="element-16061">
                              {formatDate(deduction.date)}
                            </td>
                            <td className="px-4 py-3 text-slate-600 text-xs" data-id="element-16062">
                              #{deduction.orderId}
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-900 text-xs" data-id="element-16063">
                              {deduction.reportTitle}
                            </td>
                            <td className="px-4 py-3 text-right" data-id="element-16064">
                              <div className="text-rose-500 font-bold text-xs" data-id="element-16065">
                                -1 отчет
                              </div>
                              <div className="text-[10px] text-slate-500" data-id="element-16066">
                                Остаток: {remainingAtTime}
                              </div>
                            </td>
                          </tr>;
              })}
                    </tbody>
                  </table>}
              </div>
            </div>
          </div>, document.body)}

      {/* Purchase Confirmation Modal */}
      {confirmPurchase && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md overflow-y-auto" data-id="element-16067">
            <div className="bg-white rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.3)] max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200 ring-1 ring-black/5" data-id="element-16068">
              <div className="flex items-center justify-between p-5 border-b border-slate-100" data-id="element-16069">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-16070">
                  Подтверждение покупки
                </h3>
                <button onClick={() => {
            setConfirmPurchase(null);
            setIsProcessing(false);
          }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-16071">
                  <X className="w-5 h-5" data-id="element-16072" />
                </button>
              </div>

              <div className="p-5 space-y-4" data-id="element-16073">
                <div className="flex items-center gap-4 p-4 bg-indigo-50 rounded-xl" data-id="element-16074">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0" data-id="element-16075">
                    <Package className="w-6 h-6" data-id="element-16076" />
                  </div>
                  <div data-id="element-16077">
                    <div className="text-lg font-bold text-slate-900" data-id="element-16078">
                      Пакет {confirmPurchase} отчётов
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-16079">
                      Бессрочный доступ
                    </div>
                  </div>
                </div>

                <div className="space-y-3 text-sm" data-id="element-16080">
                  <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-16081">
                    <span className="text-slate-500" data-id="element-16082">Базовая цена</span>
                    <span className={`font-medium ${discount > 0 ? 'line-through text-slate-400' : 'text-slate-900'}`} data-id="element-16083">
                      {formatPrice(BASE_PRICES[confirmPurchase])}
                    </span>
                  </div>
                  {discount > 0 && <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-16084">
                      <span className="text-emerald-600 font-medium" data-id="element-16085">
                        Скидка {discount}% ({discountReason})
                      </span>
                      <span className="text-emerald-600 font-bold" data-id="element-16086">
                        -
                        {formatPrice(BASE_PRICES[confirmPurchase] - getPackagePrice(confirmPurchase, discount))}
                      </span>
                    </div>}
                  <div className="flex justify-between py-3" data-id="element-16087">
                    <span className="text-lg font-bold text-slate-900" data-id="element-16088">
                      Итого к оплате
                    </span>
                    <span className="text-lg font-bold text-indigo-600" data-id="element-16089">
                      {formatPrice(getPackagePrice(confirmPurchase, discount))}
                    </span>
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg text-xs text-slate-500" data-id="element-16090">
                  <ShieldCheck className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-16091" />
                  <span data-id="element-16092">
                    Гарантия возврата 7 дней. Отчёты из пакета списываются при
                    оформлении заказов.
                  </span>
                </div>
              </div>

              <div className="p-5 border-t border-slate-100 space-y-3" data-id="element-16093">
                <Button variant="primary" fullWidth disabled={isProcessing} onClick={handleConfirmPurchase} className="shadow-md shadow-indigo-500/20" data-id="element-16094">
                  {isProcessing ? <span className="flex items-center gap-2" data-id="element-16095">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-16096" />
                      Обработка оплаты...
                    </span> : <span className="flex items-center gap-2" data-id="element-16097">
                      <CreditCard className="w-4 h-4" data-id="element-16098" />
                      Оплатить{' '}
                      {formatPrice(getPackagePrice(confirmPurchase, discount))}
                    </span>}
                </Button>
                <button onClick={() => {
            setConfirmPurchase(null);
            setIsProcessing(false);
          }} className="w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors py-1" data-id="element-16099">
                  Отмена
                </button>
              </div>
            </div>
          </div>, document.body)}

      {/* Purchase Success Modal */}
      {purchaseSuccess && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md overflow-y-auto" data-id="element-16100">
            <div className="bg-white rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.3)] max-w-sm w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200 text-center p-8 ring-1 ring-black/5" data-id="element-16101">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4" data-id="element-16102">
                <Check className="w-8 h-8 text-emerald-600" data-id="element-16103" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-16104">
                Пакет приобретён!
              </h3>
              <p className="text-sm text-slate-500 mb-6" data-id="element-16105">
                Пакет на {purchaseSuccess} отчётов добавлен в ваш аккаунт.
                Используйте отчёты при оформлении заказов.
              </p>
              <Button variant="primary" fullWidth onClick={() => setPurchaseSuccess(null)} data-id="element-16106">
                Отлично
              </Button>
            </div>
          </div>, document.body)}

      {/* Upgrade Plan Modal */}
      {upgradePlan && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md overflow-y-auto" data-id="element-16107">
            <div className="bg-white rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.3)] max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200 ring-1 ring-black/5" data-id="element-16108">
              <div className="p-5 border-b border-slate-100" data-id="element-16109">
                <div className="flex items-center justify-between" data-id="element-16110">
                  <div className="flex items-center gap-2" data-id="element-16111">
                    <Crown className="w-5 h-5 text-amber-500" data-id="element-16112" />
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-16113">
                      Переход на {upgradePlan.name}
                    </h3>
                  </div>
                  <button onClick={() => {
              setUpgradePlan(null);
              setUpgradeProcessing(false);
            }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-16114">
                    <X className="w-5 h-5" data-id="element-16115" />
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-5" data-id="element-16116">
                {/* What you get */}
                <div data-id="element-16117">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-16118">
                    Что вы получите
                  </div>
                  <div className="space-y-1.5" data-id="element-16119">
                    {upgradePlan.included.filter(f => !currentPlan.included.includes(f)).length > 0 ? upgradePlan.included.filter(f => !currentPlan.included.includes(f)).map(feature => <div key={feature} className="flex items-center gap-2 text-sm text-slate-700" data-id="element-16120">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-16121" />
                              {feature}
                            </div>) : upgradePlan.included.map(feature => <div key={feature} className="flex items-center gap-2 text-sm text-slate-700" data-id="element-16122">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-16123" />
                            {feature}
                          </div>)}
                  </div>
                </div>

                {/* Price */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-2" data-id="element-16124">
                  <div className="flex justify-between text-sm" data-id="element-16125">
                    <span className="text-slate-500" data-id="element-16126">
                      Текущий тариф ({currentPlan.name})
                    </span>
                    <span className="text-slate-600" data-id="element-16127">
                      {currentPlan.priceLabel}
                      {currentPlan.period}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-16128">
                    <span className="text-slate-500" data-id="element-16129">
                      Новый тариф ({upgradePlan.name})
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-16130">
                      {upgradePlan.priceLabel}
                      {upgradePlan.period}
                    </span>
                  </div>
                  {upgradePlan.price > currentPlan.price && <div className="flex justify-between text-sm pt-2 border-t border-slate-200" data-id="element-16131">
                      <span className="font-medium text-slate-700" data-id="element-16132">
                        Доплата сейчас
                      </span>
                      <span className="font-bold text-indigo-600" data-id="element-16133">
                        +
                        {(upgradePlan.price - currentPlan.price).toLocaleString('ru-RU')}{' '}
                        ₽
                      </span>
                    </div>}
                </div>

                {/* Payment Method */}
                <div data-id="element-16134">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-16135">
                    Способ оплаты
                  </div>
                  <div className="flex gap-3" data-id="element-16136">
                    <button onClick={() => setUpgradePayMethod('balance')} className={`flex-1 p-3 rounded-xl border text-left transition-all ${upgradePayMethod === 'balance' ? 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-16137">
                      <div className="flex items-center gap-2 mb-1" data-id="element-16138">
                        <Wallet className="w-4 h-4 text-indigo-600" data-id="element-16139" />
                        <span className="font-bold text-sm text-slate-900" data-id="element-16140">
                          С баланса
                        </span>
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-16141">
                        Доступно:{' '}
                        <span className="font-bold text-slate-700" data-id="element-16142">
                          {accountBalance.toLocaleString('ru-RU')} ₽
                        </span>
                      </div>
                      {upgradePayMethod === 'balance' && accountBalance < upgradePlan.price - currentPlan.price && <div className="text-[10px] text-rose-500 mt-1 flex items-center gap-1" data-id="element-16143">
                            <AlertTriangle className="w-3 h-3" data-id="element-16144" /> Недостаточно
                            средств
                          </div>}
                    </button>
                    <button onClick={() => setUpgradePayMethod('card')} className={`flex-1 p-3 rounded-xl border text-left transition-all ${upgradePayMethod === 'card' ? 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-16145">
                      <div className="flex items-center gap-2 mb-1" data-id="element-16146">
                        <CreditCard className="w-4 h-4 text-slate-600" data-id="element-16147" />
                        <span className="font-bold text-sm text-slate-900" data-id="element-16148">
                          С карты
                        </span>
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-16149">
                        Visa •••• 4582
                      </div>
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg" data-id="element-16150">
                  <Bell className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" data-id="element-16151" />
                  <div className="text-xs text-amber-700" data-id="element-16152">
                    Тариф будет изменён сразу. Следующее списание{' '}
                    <span className="font-bold" data-id="element-16153">{upgradePlan.priceLabel}</span>{' '}
                    произойдёт в дату продления подписки.
                  </div>
                </div>
              </div>

              <div className="p-5 border-t border-slate-100 space-y-3" data-id="element-16154">
                <Button variant="primary" fullWidth disabled={upgradeProcessing || upgradePayMethod === 'balance' && accountBalance < upgradePlan.price - currentPlan.price} onClick={() => {
            setUpgradeProcessing(true);
            const planName = upgradePlan.name;
            setTimeout(() => {
              setUpgradeProcessing(false);
              setUpgradePlan(null);
              showToast(`Тариф успешно изменён на ${planName}! 🎉`);
            }, 2000);
          }} className="shadow-md shadow-indigo-500/20" data-id="element-16155">
                  {upgradeProcessing ? <span className="flex items-center gap-2" data-id="element-16156">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-16157" />
                      Обработка...
                    </span> : <span className="flex items-center gap-2" data-id="element-16158">
                      <Zap className="w-4 h-4" data-id="element-16159" />
                      {upgradePlan.price > currentPlan.price ? `Оплатить и перейти на ${upgradePlan.name}` : `Перейти на ${upgradePlan.name}`}
                    </span>}
                </Button>
                <button onClick={() => {
            setUpgradePlan(null);
            setUpgradeProcessing(false);
          }} className="w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors py-1" data-id="element-16160">
                  Отмена
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}